import axios from "axios";
import { loginRequest, msalInstance } from "../authConfig";

const baseUrl = process.env.REACT_APP_RISK_MONITOR_BACKEND_URL;

export const RiskMonitorAxiosInstance = axios.create({
  baseURL: baseUrl,
  timeout: 60000,
  headers: {
    "Content-Type": "application/json",
    accept: "application/json",
  },
});

let _cachedToken = null;
let _tokenExpiry = 0;
let _msalInitialized = false;

async function ensureMsalReady() {
  if (!msalInstance) return;
  if (!_msalInitialized) {
    await msalInstance.initialize();
    _msalInitialized = true;
  }
}

async function getAccessToken() {
  await ensureMsalReady();
  if (!msalInstance) return null;

  if (_cachedToken && Date.now() < _tokenExpiry - 60_000) {
    return _cachedToken;
  }

  const account =
    msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0];
  if (!account) return null;

  msalInstance.setActiveAccount(account);

  const tokenRequest = { scopes: loginRequest.scopes, account };

  try {
    const tokenResponse = await msalInstance.acquireTokenSilent(tokenRequest);
    _cachedToken = tokenResponse.accessToken;
    _tokenExpiry =
      tokenResponse.expiresOn?.getTime() ?? Date.now() + 3_600_000;
    return _cachedToken;
  } catch {
    // Silent failed (consent / interaction). Popup waits for a token instead
    // of redirecting and letting this request go out with no Authorization.
    const tokenResponse = await msalInstance.acquireTokenPopup(tokenRequest);
    _cachedToken = tokenResponse.accessToken;
    _tokenExpiry =
      tokenResponse.expiresOn?.getTime() ?? Date.now() + 3_600_000;
    return _cachedToken;
  }
}

function isUploadRequest(config) {
  const url = `${config.baseURL || ""}${config.url || ""}`;
  if (url.includes("/uploads")) return true;
  // Publishing or deleting a methodology doc needs a real signed-in token,
  // same as a data upload. Listing/downloading falls through to the generic
  // path so a token hiccup shows the normal API error instead of a
  // sign-in-to-upload message.
  const method = (config.method || "get").toLowerCase();
  return url.includes("/methodology-docs") && method !== "get";
}

RiskMonitorAxiosInstance.interceptors.request.use(async (config) => {
  try {
    const token = await getAccessToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      return config;
    }
  } catch (e) {
    console.warn("MSAL token acquisition failed:", e.message);
    if (isUploadRequest(config)) {
      return Promise.reject(
        new Error("Sign in required to upload. Allow the login popup and retry."),
      );
    }
  }

  if (isUploadRequest(config)) {
    const onLanIp =
      typeof window !== "undefined" &&
      !["localhost", "127.0.0.1"].includes(window.location.hostname) &&
      window.location.protocol === "http:";
    const message = onLanIp
      ? "Microsoft login does not work on http://<ip>. Open the app at http://localhost:3000 instead."
      : "Sign in required to upload. Refresh the page and try again.";
    return Promise.reject(new Error(message));
  }
  return config;
});

function isCanceled(error) {
  return (
    axios.isCancel(error) ||
    error?.code === "ERR_CANCELED" ||
    error?.name === "CanceledError" ||
    error?.name === "AbortError"
  );
}

function requestConfig(params, signal) {
  return signal ? { params, signal } : { params };
}

RiskMonitorAxiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (isCanceled(error)) {
      return Promise.reject(error);
    }
    console.error("RiskMonitor API Error:", {
      message: error.message,
      status: error?.response?.status,
      baseURL: error.config?.baseURL,
    });
    return Promise.reject(error);
  },
);

export async function fetchAvailableDates() {
  const response = await RiskMonitorAxiosInstance.get("/snapshots");
  return response?.data?.data || [];
}

export async function fetchVarPercentiles(
  date,
  portfolio = null,
  includePrev = false,
  strategyTypeName = null,
  { signal } = {},
) {
  const params = { valuation_date: date, include_prev: includePrev };
  if (portfolio) params.portfolio = portfolio;
  if (strategyTypeName) params.strategy_type_name = strategyTypeName;
  const response = await RiskMonitorAxiosInstance.get(
    "/simulations/var-percentiles",
    requestConfig(params, signal),
  );
  return response?.data;
}

// Sim drill-down calls default to the simulation_pnl (MC/shock) dataset. Portfolio
// greeks sims reuse the same sim numbers, so they pass source="greeks" explicitly.
function applySimSource(params, source) {
  if (source && source !== "simulation") params.source = source;
  return params;
}

export async function fetchSimBreakdown(
  date,
  simNumber,
  portfolio = null,
  { signal, source = null } = {},
) {
  const params = applySimSource({ valuation_date: date, sim_number: simNumber }, source);
  if (portfolio) params.portfolio = portfolio;
  const response = await RiskMonitorAxiosInstance.get(
    "/simulations/sim-breakdown",
    requestConfig(params, signal),
  );
  return response?.data;
}

export async function fetchSimBreakdownComparison(
  date,
  simNumber,
  compareDate,
  compareSimNumber,
  portfolio = null,
  { signal } = {},
) {
  const params = {
    valuation_date: date,
    sim_number: simNumber,
    compare_date: compareDate,
    compare_sim_number: compareSimNumber,
  };
  if (portfolio) params.portfolio = portfolio;
  const response = await RiskMonitorAxiosInstance.get(
    "/simulations/sim-breakdown-comparison",
    requestConfig(params, signal),
  );
  return response?.data;
}

export async function fetchSimInvestmentBreakdown(
  date,
  simNumber,
  strategyType,
  portfolio = null,
  { signal, source = null } = {},
) {
  const params = applySimSource({
    valuation_date: date,
    sim_number: simNumber,
    strategy_type_name: strategyType,
  }, source);
  if (portfolio) params.portfolio = portfolio;
  const response = await RiskMonitorAxiosInstance.get(
    "/simulations/sim-investment-breakdown",
    requestConfig(params, signal),
  );
  return response?.data;
}

export async function fetchSimInvestmentDetail(
  date,
  simNumber,
  investmentCode2,
  strategyType,
  portfolio = null,
  { signal } = {},
  strategyName = null,
  source = null,
) {
  const params = applySimSource({
    valuation_date: date,
    sim_number: simNumber,
    investment_code_2: investmentCode2,
    strategy_type_name: strategyType,
  }, source);
  if (portfolio) params.portfolio = portfolio;
  if (strategyName) params.strategy_name = strategyName;
  const response = await RiskMonitorAxiosInstance.get(
    "/simulations/sim-investment-detail",
    requestConfig(params, signal),
  );
  return response?.data;
}

export async function fetchHistoricalVar(
  portfolio = null,
  strategyTypeName = null,
  { signal } = {},
) {
  const params = {};
  if (portfolio) params.portfolio = portfolio;
  if (strategyTypeName) params.strategy_type_name = strategyTypeName;
  const response = await RiskMonitorAxiosInstance.get(
    "/simulations/historical-var",
    requestConfig(params, signal),
  );
  return response?.data;
}

export async function fetchVarByStrategy(
  date,
  portfolio = null,
  includePrev = true,
  { signal } = {},
) {
  const params = { valuation_date: date, include_prev: includePrev };
  if (portfolio) params.portfolio = portfolio;
  const response = await RiskMonitorAxiosInstance.get(
    "/simulations/var-by-strategy",
    requestConfig(params, signal),
  );
  return response?.data;
}

export async function fetchPnlDistribution(
  date,
  portfolio = null,
  strategyTypeName = null,
  { signal } = {},
) {
  const params = { valuation_date: date };
  if (portfolio) params.portfolio = portfolio;
  if (strategyTypeName) params.strategy_type_name = strategyTypeName;
  const response = await RiskMonitorAxiosInstance.get(
    "/simulations/pnl-distribution",
    requestConfig(params, signal),
  );
  return response?.data;
}

export async function fetchStressScenarios(
  date,
  portfolio = null,
  strategyTypeName = null,
  { signal } = {},
) {
  const params = { valuation_date: date };
  if (portfolio) params.portfolio = portfolio;
  if (strategyTypeName) params.strategy_type_name = strategyTypeName;
  const response = await RiskMonitorAxiosInstance.get(
    "/simulations/stress-scenarios",
    requestConfig(params, signal),
  );
  return response?.data;
}

export async function fetchCustomShocks(date, portfolio = null, { signal } = {}) {
  const params = { valuation_date: date };
  if (portfolio) params.portfolio = portfolio;
  const response = await RiskMonitorAxiosInstance.get(
    "/simulations/custom-shocks",
    requestConfig(params, signal),
  );
  return response?.data;
}

// Same response shape as fetchCustomShocks ({ scenarios, extra_columns, ... }),
// read from the portfolio_greeks dataset folder on the backend.
export async function fetchPortfolioGreeks(date, portfolio = null, { signal } = {}) {
  const params = { valuation_date: date };
  if (portfolio) params.portfolio = portfolio;
  const response = await RiskMonitorAxiosInstance.get(
    "/simulations/portfolio-greeks",
    requestConfig(params, signal),
  );
  return response?.data;
}

export async function fetchSimFactors(date, simNumber, { signal } = {}) {
  const params = { valuation_date: date, sim_number: simNumber };
  const response = await RiskMonitorAxiosInstance.get(
    "/simulations/sim-factors",
    requestConfig(params, signal),
  );
  return response?.data;
}

export async function fetchRiskSummary(
  date,
  portfolio = null,
  includePrev = false,
  strategyTypeName = null,
  { signal } = {},
) {
  const params = { valuation_date: date, include_prev: includePrev };
  if (portfolio) params.portfolio = portfolio;
  if (strategyTypeName) params.strategy_type_name = strategyTypeName;
  const response = await RiskMonitorAxiosInstance.get(
    "/simulations/risk-summary",
    requestConfig(params, signal),
  );
  return response?.data;
}

export async function fetchFactorContributions(
  date,
  strategyTypeName = null,
  { signal } = {},
) {
  const params = { valuation_date: date };
  if (strategyTypeName) params.strategy_type_name = strategyTypeName;
  const response = await RiskMonitorAxiosInstance.get(
    "/simulations/factor-contributions",
    requestConfig(params, signal),
  );
  return response?.data;
}

/**
 * Fetches all firmwide risk-page data in a single HTTP round-trip.
 * Replaces the 6 parallel calls (var-percentiles, historical-var,
 * var-by-strategy, pnl-distribution, stress-scenarios, risk-summary) plus
 * factor-contributions, so they only compete with each other (not sibling
 * requests like strategy-tab prefetch) for the backend's shared blocking-work
 * capacity limiter, and share a single 60s timeout budget instead of six.
 */
export async function fetchFirmwideDashboard(
  date,
  portfolio = null,
  includePrev = true,
  { signal } = {},
) {
  const params = { valuation_date: date, include_prev: includePrev };
  if (portfolio) params.portfolio = portfolio;
  const response = await RiskMonitorAxiosInstance.get(
    "/simulations/firmwide-dashboard",
    requestConfig(params, signal),
  );
  return response?.data;
}

/**
 * Fetches all data for a strategy tab in a single HTTP round-trip.
 * Replaces the 5 parallel calls (var, pnl, stress, factors, historical-var).
 */
export async function fetchStrategyDashboard(
  date,
  strategyTypeName,
  portfolio = null,
  { signal } = {},
) {
  const params = { valuation_date: date, strategy_type_name: strategyTypeName };
  if (portfolio) params.portfolio = portfolio;
  const response = await RiskMonitorAxiosInstance.get(
    "/simulations/strategy-dashboard",
    requestConfig(params, signal),
  );
  return response?.data;
}

export async function fetchSimInvestmentComparison(
  date,
  simNumber,
  strategyType,
  compareDate,
  compareSimNumber,
  portfolio = null,
) {
  const params = {
    valuation_date: date,
    sim_number: simNumber,
    strategy_type_name: strategyType,
    compare_date: compareDate,
    compare_sim_number: compareSimNumber,
  };
  if (portfolio) params.portfolio = portfolio;
  const response = await RiskMonitorAxiosInstance.get(
    "/simulations/sim-investment-comparison",
    { params },
  );
  return response?.data;
}

export async function fetchAvailableSims(date, { signal } = {}) {
  const params = { valuation_date: date };
  const response = await RiskMonitorAxiosInstance.get(
    "/simulations/available-sims",
    requestConfig(params, signal),
  );
  return response?.data;
}

export async function fetchConvertTakeoutRisk(date, { signal } = {}) {
  const params = { valuation_date: date };
  const response = await RiskMonitorAxiosInstance.get(
    "/simulations/convert-takeout-risk",
    requestConfig(params, signal),
  );
  return response?.data;
}

export async function fetchUploadDatasets() {
  const response = await RiskMonitorAxiosInstance.get("/uploads/datasets");
  return response?.data;
}

export async function requestUploadSas({ dataset, files }) {
  const response = await RiskMonitorAxiosInstance.post("/uploads/sas", {
    dataset,
    files,
  });
  return response?.data;
}

export async function completeUpload({ dataset, blobs }) {
  const response = await RiskMonitorAxiosInstance.post("/uploads/complete", {
    dataset,
    blobs,
  });
  return response?.data;
}

// Methodology docs live in the same storage account as the risk parquet
// files, under glazer_risk/methodology_docs — the backend only mints SAS
// URLs, file bytes go browser <-> storage directly (see /uploads above).
export async function fetchMethodologyDocs() {
  const response = await RiskMonitorAxiosInstance.get("/methodology-docs");
  return response?.data?.documents ?? [];
}

export async function requestMethodologyDocSas(filename) {
  const response = await RiskMonitorAxiosInstance.post("/methodology-docs/sas", {
    filename,
  });
  return response?.data;
}

export async function completeMethodologyDocUpload(filename) {
  const response = await RiskMonitorAxiosInstance.post(
    "/methodology-docs/complete",
    { filename },
  );
  return response?.data;
}

export async function fetchMethodologyDocDownloadUrl(filename) {
  const response = await RiskMonitorAxiosInstance.get(
    `/methodology-docs/${encodeURIComponent(filename)}/download-url`,
  );
  return response?.data?.download_url;
}

export async function deleteMethodologyDoc(filename) {
  await RiskMonitorAxiosInstance.delete(
    `/methodology-docs/${encodeURIComponent(filename)}`,
  );
}

export async function refreshCache() {
  const response = await RiskMonitorAxiosInstance.post("/admin/cache/refresh");
  return response?.data;
}
